// src/pages/qc/FgSpecModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";

export function FgSpecModal({ fgId: initialFgId, spec, fgsWithoutSpec, onSave, onCancel, mandatory = false }) {
  const [fgId,    setFgId]    = useState(initialFgId ?? "");
  const [appear,  setAppear]  = useState(spec?.appearance ?? "Clear");
  const [scMin,   setScMin]   = useState(spec?.solidContentMin ?? "");
  const [scMax,   setScMax]   = useState(spec?.solidContentMax ?? "");
  const [phMin,   setPhMin]   = useState(spec?.phMin ?? "");
  const [phMax,   setPhMax]   = useState(spec?.phMax ?? "");
  const [brkMin,  setBrkMin]  = useState(spec?.brookfieldMin ?? "");
  const [brkMax,  setBrkMax]  = useState(spec?.brookfieldMax ?? "");
  const [brkSpd,  setBrkSpd]  = useState(spec?.brookfieldSpindle ?? "");
  const [brkRpm,  setBrkRpm]  = useState(spec?.brookfieldRpm ?? "");
  const [brkTemp, setBrkTemp] = useState(spec?.brookfieldTempC ?? "");
  const [b4Min,   setB4Min]   = useState(spec?.b4CupMin ?? "");
  const [b4Max,   setB4Max]   = useState(spec?.b4CupMax ?? "");
  const [denMin,  setDenMin]  = useState(spec?.densityMin ?? "");
  const [denMax,  setDenMax]  = useState(spec?.densityMax ?? "");
  const [mPh,     setMPh]     = useState(spec?.testMethodPh         ?? "ASTM-E70-19");
  const [mVisc,   setMVisc]   = useState(spec?.testMethodViscosity  ?? "ASTME3116-18");
  const [mSolid,  setMSolid]  = useState(spec?.testMethodSolidPct   ?? "ASTM-7232-06");
  const [mB4,     setMB4]     = useState(spec?.testMethodB4Cup      ?? "IS:3944");
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const isNew = !initialFgId;

  async function handleSave() {
    if (!fgId) { setErr("Select a grade"); return; }
    setSaving(true); setErr("");
    try {
      await onSave(fgId, {
        appearance:        appear,
        solidContentMin:   scMin  !== "" ? Number(scMin)  : null,
        solidContentMax:   scMax  !== "" ? Number(scMax)  : null,
        phMin:             phMin  !== "" ? Number(phMin)  : null,
        phMax:             phMax  !== "" ? Number(phMax)  : null,
        brookfieldMin:     brkMin !== "" ? Number(brkMin) : null,
        brookfieldMax:     brkMax !== "" ? Number(brkMax) : null,
        brookfieldSpindle: brkSpd !== "" ? Number(brkSpd) : null,
        brookfieldRpm:     brkRpm !== "" ? Number(brkRpm) : null,
        brookfieldTempC:   brkTemp!== "" ? Number(brkTemp): null,
        b4CupMin:          b4Min  !== "" ? Number(b4Min)  : null,
        b4CupMax:          b4Max  !== "" ? Number(b4Max)  : null,
        densityMin:        denMin !== "" ? Number(denMin) : null,
        densityMax:        denMax !== "" ? Number(denMax) : null,
        testMethodPh:        mPh    || null,
        testMethodViscosity: mVisc  || null,
        testMethodSolidPct:  mSolid || null,
        testMethodB4Cup:     mB4    || null,
      });
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  const row = (label, ...inputs) => (
    <div className="form-row">
      <span className="form-label">{label}</span>
      {inputs}
    </div>
  );
  const num = (val, set, placeholder="") => (
    <input className="form-input input-right" type="number" step="any"
      style={{ width:90 }} value={val} placeholder={placeholder}
      onChange={e => set(e.target.value)} />
  );

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">
          {mandatory ? `Add Specification — ${initialFgId}` : isNew ? "Add FG Specification" : `Edit — ${initialFgId}`}
        </h3>
        {!mandatory && <button className="btn" onClick={onCancel}>✕</button>}
      </div>
      {mandatory && (
        <div className="alert-info" style={{ margin:"0 0 12px" }}>
          A specification is required for every new FG. Please fill in the details below.
        </div>
      )}

      {isNew && (
        <div className="form-row">
          <span className="form-label">Grade</span>
          <select className="form-select" value={fgId} onChange={e => setFgId(e.target.value)} style={{ flex:1 }}>
            <option value="">Select FG…</option>
            {fgsWithoutSpec.map(f => <option key={f.id} value={f.id}>{f.id}</option>)}
          </select>
        </div>
      )}

      <div className="form-sep" />

      {row("Appearance",
        <select className="form-select" value={appear} onChange={e => setAppear(e.target.value)}>
          <option>Clear</option><option>Hazy</option>
        </select>
      )}
      {row("Solid Content (%)", num(scMin, setScMin,"min"), <span style={{ padding:"0 4px" }}>–</span>, num(scMax, setScMax,"max"))}
      {row("pH",                num(phMin, setPhMin,"min"), <span style={{ padding:"0 4px" }}>–</span>, num(phMax, setPhMax,"max"))}

      <div className="form-sep" />
      <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8, textTransform:"uppercase", letterSpacing:".05em" }}>Brookfield Viscosity (cP)</div>
      {row("Viscosity Range", num(brkMin,setBrkMin,"min"), <span style={{ padding:"0 4px" }}>–</span>, num(brkMax,setBrkMax,"max"))}
      {row("Spindle / RPM",num(brkSpd,setBrkSpd,"spindle"), num(brkRpm,setBrkRpm,"rpm"))}
      {row("Temp (°C)",    num(brkTemp,setBrkTemp))}

      <div className="form-sep" />
      <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8, textTransform:"uppercase", letterSpacing:".05em" }}>B4 Cup (seconds)</div>
      {row("Range", num(b4Min,setB4Min,"min"), <span style={{ padding:"0 4px" }}>–</span>, num(b4Max,setB4Max,"max"))}

      <div className="form-sep" />
      <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8, textTransform:"uppercase", letterSpacing:".05em" }}>Density (g/mL) <span style={{ fontWeight:400, fontSize:10 }}>— optional</span></div>
      {row("Range", num(denMin,setDenMin,"min"), <span style={{ padding:"0 4px" }}>–</span>, num(denMax,setDenMax,"max"))}

      <div className="form-sep" />
      <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8, textTransform:"uppercase", letterSpacing:".05em" }}>Test Methods (for COA)</div>
      {row("pH Method",       <input className="form-input" style={{ flex:1 }} value={mPh}    onChange={e => setMPh(e.target.value)} />)}
      {row("Viscosity Method",<input className="form-input" style={{ flex:1 }} value={mVisc}  onChange={e => setMVisc(e.target.value)} />)}
      {row("Solids Method",   <input className="form-input" style={{ flex:1 }} value={mSolid} onChange={e => setMSolid(e.target.value)} />)}
      {row("B4 Cup Method",   <input className="form-input" style={{ flex:1 }} value={mB4}    onChange={e => setMB4(e.target.value)} />)}

      {err && <div className="alert-warn" style={{ marginTop:8 }}>{err}</div>}
      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Specification"}
        </button>
      </div>
    </div>
  );
}
